import { useEffect, useState } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { EmbeddableCanvas } from '@/features/canvas/components/embeddable-canvas';
import { CanvasPreviewSnapshotter } from '@/features/canvas/components/canvas-preview-snapshotter';
import type { PointerModeProps } from '@/features/canvas/engine/hooks/use-pointer-mode';
import { getViewport } from '@/features/canvas/engine/viewport/viewport-store';
import { useCanvasAwareness } from './hooks/use-canvas-awareness';
import { useCursorAwareness } from './hooks/use-cursor-awareness';
import { useViewportPersistence } from './hooks/use-viewport-persistence';

interface CanvasAwarenessMountProps {
  doc: Y.Doc;
  awareness: Awareness;
  user: { id: string; name: string; color?: string };
  /**
   * Stable id for this canvas — used as the viewport-store keypath together
   * with `user.id`. Typically the entityId of the canvas/space being mounted.
   */
  canvasId: string;
  /**
   * Forwarded to CanvasEngine. The shell wires this when a tool is active so
   * pane clicks place a node at the click position. Optional.
   */
  onPaneClick?: (position: { x: number; y: number }, frameId: string | null) => void;
  /** Called on every viewport change — composed with the internal persistence hook. */
  onViewportChange?: (v: { x: number; y: number; zoom: number }) => void;
  /** When true, renders the DrawingOverlay for freehand input. */
  isDrawing?: boolean;
  drawingColor?: string;
  drawingWidth?: number;
  /** Opacity for the drawing stroke (0–1). Used for highlighter mode (0.4). */
  drawingOpacity?: number;
  /** When true, renders the EraserOverlay for erasing freedraw nodes. */
  isErasing?: boolean;
  /** When true, the Connector tool is active — every node body becomes a
   *  connector source. */
  connectorMode?: boolean;
  /** Pan/selection behaviour resolved from the active tool. */
  pointerMode?: PointerModeProps;
  /** Node id to auto-select on next yjsNodes update (e.g. after expand-create). */
  pendingSelectId?: string | null;
  /** When true, passes readOnly to the engine — no editing interactions. */
  readOnly?: boolean;
  /** Called on every pointermove with the current canvas-space coordinates. Used by the shell to track cursor for paste placement. */
  onCursorMove?: (point: { x: number; y: number }) => void;
  /** Wires the engine's Frames-panel toggle button. Forwarded straight to CanvasEngine. */
  onOpenFramesPanel?: () => void;
  framesPanelOpen?: boolean;
}

interface RestoredViewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * Shell mount for user-derived canvas state: awareness (remote cursors +
 * selection) and viewport persistence (per-user restore + debounced save).
 *
 * Lives in shell because both surfaces depend on session/auth (`user`) and
 * IndexedDB (`canvasId + user.id` keypath). The engine itself stays free of
 * session and storage concerns — it accepts the resolved values as props.
 *
 * Viewport restore (Option C from the M10 review): loads the saved viewport
 * from IDB once on mount via `getViewport`, holds the engine in a "loading"
 * placeholder until the read resolves, then renders the engine with
 * `defaultViewport` set. This keeps the seam clean — engine reads
 * `defaultViewport` as a regular prop, no imperative `setViewport` handoff.
 */
export function CanvasAwarenessMount({
  doc,
  awareness,
  user,
  canvasId,
  onPaneClick,
  onViewportChange: onViewportChangeProp,
  isDrawing,
  drawingColor,
  drawingWidth,
  drawingOpacity,
  isErasing,
  connectorMode,
  pointerMode,
  pendingSelectId,
  readOnly,
  onCursorMove,
  onOpenFramesPanel,
  framesPanelOpen,
}: CanvasAwarenessMountProps) {
  const { remoteStates } = useCanvasAwareness({ awareness, user });
  const { reportPointer, reportSelection, reportEdgeSelection, reportInFlightEdge } = useCursorAwareness({ awareness });
  const { onViewportChange: persistViewport } = useViewportPersistence({ canvasId, userId: user.id });

  const onViewportChange = onViewportChangeProp
    ? (v: { x: number; y: number; zoom: number }) => { persistViewport(v); onViewportChangeProp(v); }
    : persistViewport;

  // One-shot async restore. `null` = still loading; `false` = no entry; otherwise the saved viewport.
  type RestoreState = null | false | RestoredViewport;
  const [restored, setRestored] = useState<RestoreState>(null);
  useEffect(() => {
    let cancelled = false;
    void getViewport(canvasId, user.id).then((entry) => {
      if (cancelled) return;
      setRestored(entry ? { x: entry.x, y: entry.y, zoom: entry.zoom } : false);
    });
    return () => {
      cancelled = true;
    };
  }, [canvasId, user.id]);

  // Hold render until the IDB read resolves so the engine doesn't paint with
  // (0, 0, zoom 1) and then jump to the restored position. The wait is one
  // microtask in jsdom + a single IDB roundtrip in production.
  if (restored === null) return null;

  return (
    <EmbeddableCanvas
      doc={doc}
      awareness={awareness}
      readOnly={readOnly}
      remoteStates={remoteStates}
      reportPointer={reportPointer}
      reportSelection={reportSelection}
      reportEdgeSelection={reportEdgeSelection}
      reportInFlightEdge={reportInFlightEdge}
      onCursorMove={onCursorMove}
      defaultViewport={restored === false ? undefined : restored}
      onViewportChange={onViewportChange}
      onPaneClick={onPaneClick}
      isDrawing={isDrawing}
      drawingColor={drawingColor}
      drawingWidth={drawingWidth}
      drawingOpacity={drawingOpacity}
      isErasing={isErasing}
      connectorMode={connectorMode}
      pointerMode={pointerMode}
      pendingSelectId={pendingSelectId}
      onOpenFramesPanel={onOpenFramesPanel}
      framesPanelOpen={framesPanelOpen}
      inFlowSlot={
        <CanvasPreviewSnapshotter doc={doc} documentId={canvasId} enabled={!readOnly} />
      }
    />
  );
}
